import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, Loader2, Plus, Landmark } from "lucide-react";
import { toast } from "sonner";

const PRODUCER = ["farmer", "cooperative", "collective_executive", "admin"];

const CoopForm = ({ city, onCreated }) => {
  const [f, setF] = useState({ name: "", city, members: 50, products: "Wheat, Lentil", description: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/commons/coops", { ...f, members: +f.members, products: f.products.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Cooperative registered and anchored"); onCreated();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const cls = "bg-[#050A15] border border-white/10 px-2 py-1.5 text-xs outline-none focus:border-[#00FF7F] w-full";
  return (
    <form onSubmit={submit} className="qs-panel p-4 grid md:grid-cols-5 gap-2 items-end" data-testid="coop-form">
      <input data-testid="coop-name" className={cls} placeholder="Cooperative name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
      <input data-testid="coop-members" className={cls} type="number" value={f.members} onChange={(e) => setF({ ...f, members: e.target.value })} />
      <input data-testid="coop-products" className={cls} value={f.products} onChange={(e) => setF({ ...f, products: e.target.value })} placeholder="Products, comma-separated" />
      <input className={cls} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description" />
      <button data-testid="coop-submit" disabled={busy} className="qs-cta bg-[#00FF7F] text-[#050A15] font-semibold px-3 py-1.5 text-xs flex items-center justify-center gap-1">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5" /> Register in {city}</>}
      </button>
    </form>
  );
};

export const CoopsPanel = ({ city, coops, onChange }) => {
  const { user } = useAuth();
  const [col, setCol] = useState(null);
  useEffect(() => { api.get("/commons/collective", { params: { city } }).then((r) => setCol(r.data)).catch(() => setCol(null)); }, [city, coops]);

  return (
    <div className="space-y-4" data-testid="coops-panel">
      {col && (
        <div className="qs-grad-border p-5" data-testid="collective-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#8A2BE2]"><Landmark className="h-4 w-4" /> Collective company</div>
          <h3 className="font-display font-bold text-2xl mt-1">{col.name}</h3>
          <p className="text-xs text-slate-400 mt-1 max-w-3xl">{col.charter}</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 font-mono text-sm">
            {[["Cooperatives", col.coops.length], ["Members", col.members], ["Lots", col.lots], ["Traded kg", col.traded_kg], ["Farmer revenue", `${col.farmer_revenue_try.toLocaleString()} TRY`]].map(([k, v]) => (
              <div key={k} className="bg-[#050A15] border border-white/5 p-2"><div className="text-[#00FF7F] font-bold">{v}</div><div className="text-[10px] text-slate-500 uppercase">{k}</div></div>
            ))}
          </div>
          <div className="text-xs text-slate-500 mt-3">Executives: {col.executives.map((e) => e.name).join(", ") || "—"} · Products: {col.products.join(", ") || "—"}</div>
        </div>
      )}
      {user && PRODUCER.includes(user.role) && <CoopForm city={city} onCreated={onChange} />}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {coops.map((c) => (
          <div key={c.id} className="qs-panel p-4" data-testid={`coop-${c.id}`}>
            <div className="flex items-center gap-2 font-display font-semibold"><Users className="h-4 w-4 text-[#00FF7F]" /> {c.name}</div>
            <div className="text-xs text-slate-500 mt-1">{c.members} members · {c.products.join(", ")}</div>
            <p className="text-xs text-slate-400 mt-2">{c.description}</p>
            <div className="text-[10px] font-mono text-slate-600 mt-2 truncate">anchor {c.anchor}</div>
          </div>
        ))}
        {!coops.length && <p className="text-sm text-slate-500">No cooperatives in {city} yet.</p>}
      </div>
    </div>
  );
};
